import AttachmentIcon from '@mui/icons-material/Attachment';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { colors, CustomPagination, GET_INSTALLER_INSTALLATIONS_LIST } from '@watter/shared-components';
import { installationEnums } from '@watter/shared-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { CommonTable, Details } from '../../components/common-styles';
import { CustomSlider } from '../../components/slider/slider';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { formatDateWithTimeWithSeconds } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { MainWrapper } from '../common-styles';
import { InstallationListData, IRowData } from './type';

const details = [
    { label: 'Installer Name', key: 'username' },
    { label: 'License No.', key: 'license_number' },
    { label: 'Contact No.', key: 'phone_number' },
    { label: 'Email', key: 'email' },
    { label: 'Average Installation', key: 'avg_installation_time' },
    { label: 'Last Login Date', key: 'last_logged_at' },
];
const statusColors: Record<string, string> = {
    Created: colors.lightBlue,
    Initiated: colors.pending,
    Inactive: colors.statusGray,
    Completed: colors.success,
    'In progress': colors.statusDarkBlue,
};
const InstallerDetails: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = React.useState(false);
    const [listData, setInstallerInstallationsListData] = useState<IRowData[]>([]);
    const [totalCount, setTotalRecordCount] = React.useState(0);
    const installerData = location.state?.installerData;
    const installerUserId = installerData?.id;
    const rowsPerPage = 5;
    const [page, setCurrentPage] = React.useState(1);
    const dispatch = useDispatch();
    const tableRef = useRef<HTMLInputElement>(null);
    const getAddress = (...data: any) => {
        return [...data.filter(Boolean)].join(', ');
    };
    const installationStatusMap = React.useMemo<Record<number, string>>(
        () => ({
            [installationEnums.InstallationStatus.initiated]: 'Initiated',
            [installationEnums.InstallationStatus.inprogress]: 'In progress',
            [installationEnums.InstallationStatus.inactive]: 'Inactive',
            [installationEnums.InstallationStatus.completed]: 'Completed',
        }),
        [],
    );
    const getStatusText = React.useCallback(
        (status: number) => installationStatusMap[status] || 'Created',
        [installationStatusMap],
    );

    const fetchInstallerInstallationsList = useCallback(async () => {
        try {
            dispatch(showLoader());
            // eslint-disable-next-line no-undef
            const { data } = await client.query<{
                // eslint-disable-next-line no-undef
                getInstallerInstallationsList: {
                    // eslint-disable-next-line no-use-before-define
                    data: InstallationListData[];
                    // eslint-disable-next-line no-undef
                    total_count: number;
                };
            }>({
                query: GET_INSTALLER_INSTALLATIONS_LIST,
                variables: {
                    limit: rowsPerPage,
                    page,
                    id: installerUserId,
                },
                fetchPolicy: 'network-only',
            });
            dispatch(hideLoader());
            if (data?.getInstallerInstallationsList?.data) {
                const formattedRows: IRowData[] = data.getInstallerInstallationsList.data.map(row => ({
                    id: row.id,
                    username: row.username || '-',
                    location: getAddress(row.state, row.city, row.street, row.zip_code) || '-',
                    installation_started_at: row.installation_started_at
                        ? formatDateWithTimeWithSeconds(row?.installation_started_at)
                        : '-',
                    installation_date: formatDateWithTimeWithSeconds(row?.installation_date),
                    installation_time: row.installation_time || '-',
                    status: getStatusText(row.status),
                }));
                setInstallerInstallationsListData(formattedRows);
                setTotalRecordCount(data?.getInstallerInstallationsList?.total_count);
            } else {
                console.warn('No data found in API response');
            }
        } catch (err: any) {
            dispatch(hideLoader());
            console.error(err, 'err in fetching Installer Installation List');
        } finally {
            dispatch(hideLoader());
        }
    }, [page, dispatch, rowsPerPage, installerUserId, getStatusText]);

    useEffect(() => {
        if (installerUserId) {
            setInstallerInstallationsListData([]);
            fetchInstallerInstallationsList();
            if (tableRef.current) {
                tableRef.current.scrollIntoView();
            }
        }
    }, [installerUserId, page, fetchInstallerInstallationsList]);

    const columns: GridColDef[] = [
        { field: 'username', headerName: 'Customer Name', flex: 1 },
        { field: 'location', headerName: 'Location', flex: 1 },
        { field: 'installation_date', headerName: 'Scheduled Installation', flex: 0.8 },
        { field: 'installation_started_at', headerName: 'Actual Installation', flex: 0.7 },
        { field: 'installation_time', headerName: 'Installation Time', flex: 0.7 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.4,
            headerAlign: 'center',
            align: 'center',
            renderCell: params => (
                <Stack direction="row" height="100%" alignItems="center" justifyContent="center">
                    <Typography
                        variant="subtitle1"
                        textAlign="center"
                        className="statusText"
                        bgcolor={statusColors[params.value] || colors.pending}
                    >
                        {params.value}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: 'actions',
            headerName: 'View Attachments',
            sortable: false,
            flex: 0.7,
            headerAlign: 'right',
            align: 'right',
            cellClassName: 'cstRemovePointer',
            renderCell: () => (
                <Box display="flex" justifyContent="end">
                    <IconButton
                        disabled
                        // onClick={e => {
                        //     setOpen(true);
                        // }}
                    >
                        <AttachmentIcon />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const sliderContent = () => {
        return (
            <Box height="100%">
                <Typography variant="h4">{`${t('attachments')}`}</Typography>
                <MainWrapper className="detailsForm">
                    <Box height="100%" className="details profileDetails">
                        <Typography variant="h6">{`${t('pre_installation_images')}`}</Typography>
                        <Typography variant="h6">{`${t('post_installation_images')}`}</Typography>
                    </Box>
                </MainWrapper>
            </Box>
        );
    };

    return (
        <>
            <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
                <Box display="flex" alignItems="center">
                    <IconButton
                        onClick={() => {
                            navigate(NavigationConfig.ManageInstaller);
                        }}
                    >
                        <KeyboardBackspaceIcon />
                    </IconButton>{' '}
                    <Typography variant="h4" pl={1}>
                        Installer Details
                    </Typography>
                </Box>
                {/* <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography>{t('user_status')}</Typography>
                    <AntSwitch checked={true} />
                </Stack> */}
            </Stack>
            <Details>
                <Grid container>
                    {installerData ? (
                        details.map(detail => (
                            <Grid key={detail.key} size={{ xs: 12, sm: 4 }}>
                                <Typography variant="subtitle1">{detail.label}</Typography>
                                <Typography variant="h5">{installerData[detail.key] || '-'}</Typography>
                            </Grid>
                        ))
                    ) : (
                        <Typography>{`${t('no_data_available')}`}</Typography>
                    )}
                </Grid>
            </Details>
            <CommonTable height="calc(100vh - 387px)">
                <Paper sx={{ width: '100%' }} className="cstInstaller">
                    <DataGrid
                        rows={listData}
                        columns={columns}
                        disableColumnMenu
                        disableColumnSorting
                        hideFooter
                        ref={tableRef}
                    />
                    {totalCount > 0 && (
                        <CustomPagination
                            totalCount={totalCount}
                            rowsPerPage={rowsPerPage}
                            currentPage={page}
                            onPageChange={setCurrentPage} // Passing state setter function
                        />
                    )}
                    <CustomSlider sliderOpen={open} sliderClose={() => setOpen(false)} drawerList={sliderContent()} />
                </Paper>
            </CommonTable>
        </>
    );
};
export { InstallerDetails };
